import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createAppointmentWithRoom,
  createLocalPatient,
  fetchLocalPatients,
} from "../redux/thunks/localPatient.thunks";
import {
  selectAllLocalPatients,
  selectLocalPatientsLoading,
  selectLocalPatientsLoaded,
  selectLocalPatientsError,
} from "../redux/selectors/localPatient.selectors";

const normalizeSource = (source) => String(source || "local").trim().toLowerCase();

const splitFullName = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

export default function useBookAppointment(doctorIdOverride = null) {
  const dispatch = useDispatch();
  const currentDoctor = useSelector((state) => state.doctor?.user);
  const doctorId =
    doctorIdOverride ||
    currentDoctor?.doctorId ||
    currentDoctor?.pgId ||
    currentDoctor?.id;

  const localPatients = useSelector(selectAllLocalPatients);
  const loadingLocalPatients = useSelector(selectLocalPatientsLoading);
  const localPatientsLoaded = useSelector(selectLocalPatientsLoaded);
  const localPatientsError = useSelector(selectLocalPatientsError);

  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null);

  useEffect(() => {
    if (doctorId && !localPatientsLoaded && !loadingLocalPatients) {
      dispatch(fetchLocalPatients({ activeOnly: true }));
    }
  }, [doctorId, dispatch, localPatientsLoaded, loadingLocalPatients]);

  const bookAppointment = useCallback(
    async ({ patient, selectedDate, selectedTime, selectedEndTime, reason }) => {
      // FIX : message d'erreur précis au lieu du message générique,
      // pour savoir immédiatement quel champ manque.
      const missing = [];
      if (!patient) missing.push("patient");
      if (!selectedDate) missing.push("selectedDate");
      if (!selectedTime) missing.push("selectedTime");
      if (!doctorId) missing.push("doctorId");

      if (missing.length > 0) {
        const message = `Missing required information: ${missing.join(", ")}`;
        console.error("[useBookAppointment] bookAppointment aborted -", message, {
          patient,
          selectedDate,
          selectedTime,
          doctorId,
        });
        setBookingError(message);
        throw new Error(message);
      }

      setBookingLoading(true);
      setBookingError(null);

      try {
        // Waiting-room cards use `id` for the room/appointment entity. The
        // appointment endpoint needs the actual patient/user identifier.
        let patientId =
          patient.principalPatientId ||
          patient.patientId ||
          patient.userId ||
          patient.linkedUserId ||
          patient.ownerId ||
          patient.id;
        let subprofileId = patient.subprofileId || patient.subProfileId || null;

        if (!subprofileId && normalizeSource(patient.source) === "subprofile") {
          subprofileId = patient.id;
        }

        if (patient.isNew) {
          const { firstName, lastName } = splitFullName(patient.name);
          const dateOfBirth = patient.dateOfBirth
            ? new Date(patient.dateOfBirth)
            : null;

          const isoDateOfBirth =
            dateOfBirth && !Number.isNaN(dateOfBirth.getTime())
              ? dateOfBirth.toISOString().split("T")[0]
              : null;

          const createdPatient = await dispatch(
            createLocalPatient({
              firstName,
              lastName,
              dateOfBirth: isoDateOfBirth || new Date().toISOString().split("T")[0],
              email: patient.email || "",
              phone: patient.phone || "",
              gender: patient.gender || "Male",
            })
          ).unwrap();

          patientId = createdPatient?.id;
          if (!patientId) {
            throw new Error("Local patient creation did not return an id");
          }
        }

        // Calculate endTime - use selectedEndTime if provided (15-min slots), otherwise 1 hour
        const startDate = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          parseInt(selectedTime.split(":")[0], 10),
          parseInt(selectedTime.split(":")[1], 10)
        );

        let endDate;
        if (selectedEndTime) {
          endDate = new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            parseInt(selectedEndTime.split(":")[0], 10),
            parseInt(selectedEndTime.split(":")[1], 10)
          );
        } else {
          endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
        }

        const appointmentData = {
          startTime: startDate.toISOString(),
          endTime: endDate.toISOString(),
          type: "standard",
          reason: reason || "Consultation",
          ...(subprofileId ? { subprofileId: String(subprofileId) } : {}),
        };

        const response = await dispatch(
          createAppointmentWithRoom({
            patientId,
            doctorId,
            appointmentData,
          })
        ).unwrap();

        const bookedPatient = {
          id: response.room?.roomId || response.appointment?.id,
          roomId: response.room?.roomId,
          appointmentId: response.appointment?.id,
          patientId,
          subprofileId:
            response.appointment?.subprofileId ||
            response.appointment?.subProfileId ||
            subprofileId ||
            null,
          effectiveProfileId:
            response.appointment?.effectiveProfileId ||
            subprofileId ||
            patientId,
          name: patient.name,
          firstName: patient.firstName || "",
          lastName: patient.lastName || "",
          age: patient.age || 30,
          source: normalizeSource(patient.source),
          time: selectedTime,
          reason: reason || "Consultation",
          paymentStatus: "unpaid",
          amount: "",
          status: "confirmed",
          waitingTime: null,
        };

        return bookedPatient;
      } catch (error) {
        const message = error?.message || "Failed to book appointment";
        setBookingError(message);
        throw error;
      } finally {
        setBookingLoading(false);
      }
    },
    [dispatch, doctorId]
  );

  return {
    doctorId,
    localPatients,
    localPatientsLoading: loadingLocalPatients,
    localPatientsError,
    bookingLoading,
    bookingError,
    bookAppointment,
  };
}